import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAdminData } from '../admin/AdminDataContext';
import { AiPanel } from '../components/map/AiPanel';
import { CoordinatesReadout } from '../components/map/CoordinatesReadout';
import { DrawModeBanner } from '../components/map/DrawModeBanner';
import { LayersDrawer } from '../components/map/LayersDrawer';
import { LayerTableSheet } from '../components/map/LayerTableSheet';
import { MapBrandWatermark } from '../components/map/MapBrandWatermark';
import { MapControls } from '../components/map/MapControls';
import { MapSearch } from '../components/map/MapSearch';
import { ObjectCalculatorPanel } from '../components/map/ObjectCalculatorPanel';
import { OverlapPickerMenu } from '../components/map/OverlapPickerMenu';
import { PrimaryNavRail } from '../components/map/PrimaryNavRail';
import { SelectedObjectCard } from '../components/map/SelectedObjectCard';
import { api } from '../lib/api';
import type { Me } from '../lib/types';
import { MapCanvas } from '../map/MapCanvas';
import { MapProvider, useMapContext } from '../map/MapContext';
import type { DemoFeatureProps, LayerConfig } from '../map/demoLayers';
import { loadRealLayerConfigs } from '../map/realLayers';

function initialsOf(me: Me | null): string {
  if (!me) return '…';
  const a = me.first_name?.[0] ?? '';
  const b = me.last_name?.[0] ?? '';
  return (a + b).toUpperCase() || '??';
}

type FocusRequest = { focusGeometry: { type: string; coordinates?: unknown }; focusProperties: DemoFeatureProps };

/** Consumes a one-shot "fly to this object" request passed via router state
 * (e.g. from the layer registry table's "Перейти к объекту") once the map is ready. */
function FocusFromNavigation({ focus }: { focus: FocusRequest | null }) {
  const { flyToSearchHit } = useMapContext();

  useEffect(() => {
    if (focus) flyToSearchHit(focus.focusGeometry, focus.focusProperties);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}

export function MapPage() {
  const location = useLocation();
  const focus = (location.state as Partial<FocusRequest> | null)?.focusGeometry
    ? (location.state as FocusRequest)
    : null;

  const [me, setMe] = useState<Me | null>(null);
  const [layersOpen, setLayersOpen] = useState(true);
  const [aiOpen, setAiOpen] = useState(false);
  const { mapSettings, basemapsLoading } = useAdminData();
  const tools = mapSettings?.tools;
  const searchEnabled = tools?.search !== false;
  const aiEnabled = tools?.aiAnalysis !== false;

  const [realLayers, setRealLayers] = useState<LayerConfig[] | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    api.getMe().then(setMe).catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!mapSettings?.id) {
      setRealLayers([]);
      return;
    }
    loadRealLayerConfigs(mapSettings.id)
      .then((configs) => {
        if (!cancelled) setRealLayers(configs);
      })
      .catch(() => {
        if (!cancelled) setRealLayers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [mapSettings?.id, reloadToken]);

  if (realLayers === null || basemapsLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-bg-canvas">
        <p className="text-sm text-text-secondary">Загрузка карты…</p>
      </div>
    );
  }

  return (
    <MapProvider initialRealLayers={realLayers}>
      <FocusFromNavigation focus={focus} />
      <div className="flex h-screen w-full overflow-hidden bg-bg-canvas">
        <PrimaryNavRail userInitials={initialsOf(me)} userAvatarUrl={me?.avatar_url} />

        <div className="relative min-w-0 flex-1">
          <MapCanvas className="absolute inset-0" />
          <MapBrandWatermark />

          {searchEnabled && <MapSearch />}
          <LayersDrawer
            open={layersOpen}
            onToggle={() => setLayersOpen((v) => !v)}
            onLayersChanged={() => setReloadToken((t) => t + 1)}
          />
          <MapControls />
          <SelectedObjectCard />
          <ObjectCalculatorPanel />
          <OverlapPickerMenu />
          <CoordinatesReadout />
          <DrawModeBanner />
          <LayerTableSheet />
        </div>

        {aiEnabled && <AiPanel open={aiOpen} onToggle={() => setAiOpen((v) => !v)} />}
      </div>
    </MapProvider>
  );
}
